import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { ArrowLeft, FileText, Download, Loader2, MapPin, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ClientInvoicesPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        const res = await axios.get(`${API}/invoices/list`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setInvoices(res.data);
      } catch (err) {
        console.error('Error fetching invoices:', err);
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchInvoices();
  }, [token]);

  const downloadInvoice = async (bookingId) => {
    setDownloading(bookingId);
    try {
      const res = await axios.get(`${API}/invoices/booking/${bookingId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `facture-${bookingId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Facture téléchargée');
    } catch (err) {
      toast.error('Erreur lors du téléchargement');
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b]">
      <header className="px-4 py-4 border-b border-zinc-800">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <button onClick={() => navigate('/client')} className="p-2 rounded-lg hover:bg-zinc-800 transition-colors" data-testid="back-client-btn">
            <ArrowLeft className="w-5 h-5 text-zinc-400" />
          </button>
          <div className="flex items-center gap-2">
            <img src="/logo-youmi.png" alt="YOUMI" className="w-8 h-8" />
            <h1 className="text-lg font-bold text-white font-['Outfit']">Mes Factures</h1>
          </div>
        </div>
      </header>

      <main className="px-4 py-6 max-w-2xl mx-auto">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 text-emerald-500 animate-spin" />
          </div>
        ) : invoices.length === 0 ? (
          <div className="text-center py-16">
            <FileText className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400">Aucune facture disponible</p>
            <p className="text-zinc-600 text-sm mt-1">Les factures sont générées pour les courses terminées.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="invoices-list">
            <p className="text-zinc-500 text-sm mb-4">{invoices.length} facture(s) disponible(s)</p>
            {invoices.map((inv) => {
              const date = inv.pickup_datetime ? new Date(inv.pickup_datetime) : null;
              return (
                <div key={inv.id} className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4" data-testid={`invoice-${inv.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <Calendar className="w-4 h-4 text-zinc-500 shrink-0" />
                        <span className="text-white text-sm font-medium">
                          {date ? date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'}
                        </span>
                      </div>
                      <div className="flex items-start gap-2 mb-2">
                        <MapPin className="w-4 h-4 text-zinc-500 mt-0.5 shrink-0" />
                        <div>
                          <p className="text-zinc-300 text-sm truncate">{inv.pickup_address?.split(',')[0] || '-'}</p>
                          <p className="text-zinc-500 text-xs truncate">→ {inv.destination_address?.split(',')[0] || '-'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-emerald-500 font-bold">{parseFloat(inv.price || 0).toFixed(2)} €</span>
                        {inv.type === 'quick_booking' && (
                          <span className="px-2 py-0.5 rounded text-xs bg-orange-500/20 text-orange-400">Rapide</span>
                        )}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => downloadInvoice(inv.id)}
                      disabled={downloading === inv.id}
                      className="bg-emerald-600 hover:bg-emerald-500 text-white shrink-0"
                      data-testid={`download-invoice-${inv.id}`}
                    >
                      {downloading === inv.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <><Download className="w-4 h-4 mr-1" /> PDF</>
                      )}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default ClientInvoicesPage;
